import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { billingService } from '../services/api/billingService';
import type {
  BillingOverview,
  SubscriptionPlan,
  Subscription,
  PaymentMethod,
  SubscriptionBenefits,
} from '../types/billing';
import { useUser } from './UserContext';

interface BillingContextType {
  // State
  overview: BillingOverview | null;
  plans: SubscriptionPlan[];
  subscription: Subscription | null;
  paymentMethods: PaymentMethod[];
  benefits: SubscriptionBenefits | null;
  isLoading: boolean;
  error: string | null;
  stripePublishableKey: string | null;

  // Actions
  refresh: () => Promise<void>;
  subscribe: (planId: number, paymentMethodId: string) => Promise<Subscription>;
  changePlan: (newPlanId: number) => Promise<Subscription>;
  cancel: (immediately?: boolean, reason?: string) => Promise<Subscription>;
  reactivate: () => Promise<Subscription>;
  addPaymentMethod: (pmId: string, setDefault?: boolean) => Promise<PaymentMethod>;
  removePaymentMethod: (id: number) => Promise<boolean>;
  setDefaultPaymentMethod: (id: number) => Promise<boolean>;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export function BillingProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn, isAuthReady } = useUser();
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [benefits, setBenefits] = useState<SubscriptionBenefits | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!isLoggedIn || !isAuthReady) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [configData, overviewData, plansData, pmData, benefitsData] = await Promise.all([
        billingService.getBillingConfig(),
        billingService.getBillingOverview(),
        billingService.getAvailablePlans(),
        billingService.getPaymentMethods(),
        billingService.getBenefits(),
      ]);

      if (configData?.publishableKey) {
        setStripePublishableKey(configData.publishableKey);
      }

      setOverview(overviewData);
      setPlans(plansData);
      setSubscription(overviewData?.currentSubscription || null);
      setPaymentMethods(pmData);
      setBenefits(benefitsData);
    } catch (e) {
      console.error('Billing refresh error:', e);
      setError('Failed to load billing information');
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn, isAuthReady]);

  useEffect(() => {
    if (isLoggedIn && isAuthReady) {
      refresh();
    } else if (!isLoggedIn) {
      // Reset state when logged out
      setOverview(null);
      setPlans([]);
      setSubscription(null);
      setPaymentMethods([]);
      setBenefits(null);
      setIsLoading(false);
    }
  }, [isLoggedIn, isAuthReady, refresh]);

  const subscribe = useCallback(async (planId: number, paymentMethodId: string): Promise<Subscription> => {
    const newSubscription = await billingService.createSubscription({ planId, paymentMethodId });
    setSubscription(newSubscription);
    await refresh();
    return newSubscription;
  }, [refresh]);

  const changePlan = useCallback(async (newPlanId: number): Promise<Subscription> => {
    const updatedSubscription = await billingService.changePlan(newPlanId);
    setSubscription(updatedSubscription);
    await refresh();
    return updatedSubscription;
  }, [refresh]);

  const cancel = useCallback(async (immediately?: boolean, reason?: string): Promise<Subscription> => {
    const cancelledSubscription = await billingService.cancelSubscription(immediately, reason);
    setSubscription(cancelledSubscription);
    await refresh();
    return cancelledSubscription;
  }, [refresh]);

  const reactivate = useCallback(async (): Promise<Subscription> => {
    const reactivatedSubscription = await billingService.reactivateSubscription();
    setSubscription(reactivatedSubscription);
    await refresh();
    return reactivatedSubscription;
  }, [refresh]);

  const addPaymentMethod = useCallback(async (pmId: string, setDefault?: boolean): Promise<PaymentMethod> => {
    const newPm = await billingService.addPaymentMethod(pmId, setDefault ?? true);
    await refresh();
    return newPm;
  }, [refresh]);

  const removePaymentMethod = useCallback(async (id: number): Promise<boolean> => {
    const success = await billingService.removePaymentMethod(id);
    if (success) {
      await refresh();
    }
    return success;
  }, [refresh]);

  const setDefaultPaymentMethod = useCallback(async (id: number): Promise<boolean> => {
    const success = await billingService.setDefaultPaymentMethod(id);
    if (success) {
      await refresh();
    }
    return success;
  }, [refresh]);

  return (
    <BillingContext.Provider
      value={{
        overview,
        plans,
        subscription,
        paymentMethods,
        benefits,
        isLoading,
        error,
        stripePublishableKey,
        refresh,
        subscribe,
        changePlan,
        cancel,
        reactivate,
        addPaymentMethod,
        removePaymentMethod,
        setDefaultPaymentMethod,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling() {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within BillingProvider');
  }
  return context;
}
